import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from 'react';
import { useIntl } from 'react-intl';

import { dontUpsell } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Overlay } from '@trello/nachos/overlay';
import { RouteId, useRouteId } from '@trello/router';

import { MultiWorkspacePlanSelectionOverlay } from 'app/src/components/FreeTrial';
import { isEligibleForTrial } from 'app/src/components/FreeTrial/useStartFreeTrialForOrg';
import { useEligibleWorkspacesToUpgradeQuery } from 'app/src/components/SelectWorkspaceToUpgrade';
import { useIsPaidUser } from '../useIsPaidUser';
import { usePersonalProductivityCohortValue } from '../usePersonalProductivityCohortValue';
import { GAWelcomeModalWithTour } from './GAWelcomeModalWithTour';
import { GAWelcomeModalWithTrial } from './GAWelcomeModalWithTrial';
import { GAWelcomeModalWithUpgrade } from './GAWelcomeModalWithUpgrade';

import styles from './GAWelcomeModal.less';

const noop = () => {};

export const GAWelcomeModal: FunctionComponent = () => {
  const [isModalOrFlagOpen, setIsModalOrFlagOpen] = useState(true);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const intl = useIntl();
  const { value: isGateEnabled } = useFeatureGate('trello_beta_ga');
  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const isPaidUser = useIsPaidUser();
  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const routeId = useRouteId();
  const isBoardRoute = routeId === RouteId.BOARD;

  const hasSeenOnboarding = isOneTimeMessageDismissed(
    'split-screen-onboarding',
  );
  const gaViaDirectSignup = isOneTimeMessageDismissed('new-user-onboarding');

  const showModalOrFlag =
    isModalOrFlagOpen &&
    !isUpsellModalOpen &&
    !gaViaDirectSignup &&
    !isOneTimeMessageDismissed('beta-ga-welcome-modal') &&
    isGateEnabled &&
    personalProductivityCohort === 'ga' &&
    isBoardRoute;

  const { workspaces: eligibleWorkspaces, loading: loadingEligibleWorkspaces } =
    useEligibleWorkspacesToUpgradeQuery({
      skip: !showModalOrFlag || isPaidUser,
    });

  const cannotUpgradeOrStartTrial =
    eligibleWorkspaces.length === 0 && !loadingEligibleWorkspaces;

  const shouldShowFlag =
    showModalOrFlag &&
    hasSeenOnboarding &&
    (isPaidUser || dontUpsell() || cannotUpgradeOrStartTrial);

  const shouldShowModal =
    showModalOrFlag && !shouldShowFlag && !loadingEligibleWorkspaces;

  const isFreeTrialEligible = useMemo(
    () => Boolean(eligibleWorkspaces?.some(isEligibleForTrial)),
    [eligibleWorkspaces],
  );

  const onClose = useCallback(() => {
    setIsModalOrFlagOpen(false);
    setIsUpsellModalOpen(false);
    dismissOneTimeMessage('beta-ga-welcome-modal');
  }, [dismissOneTimeMessage]);

  const onContinueToUpsell = useCallback(() => {
    setIsUpsellModalOpen(true);
  }, []);

  useEffect(() => {
    if (shouldShowFlag) {
      showFlag({
        id: 'gaWelcomeFlag',
        title: intl.formatMessage({
          id: 'templates.beta_ga.the-new-trello-is-officially',
          defaultMessage: 'The new Trello is officially here!',
          description: 'Flag for GA welcome',
        }),
        description: intl.formatMessage({
          id: 'templates.beta_ga.trello-beta-has-ended',
          defaultMessage:
            'Trello Beta has ended, and the new Trello is now live for everyone.',
          description: 'Flag for GA welcome',
        }),
        appearance: 'success',
      });
      onClose();
    }
  }, [intl, onClose, shouldShowFlag]);

  const ModalComponent = useMemo(() => {
    let modal;

    if (!hasSeenOnboarding) {
      modal = <GAWelcomeModalWithTour onClose={onClose} />;
    } else if (isFreeTrialEligible) {
      modal = (
        <GAWelcomeModalWithTrial
          onClose={onClose}
          onContinue={onContinueToUpsell}
        />
      );
    } else {
      modal = (
        <GAWelcomeModalWithUpgrade
          onClose={onClose}
          onContinue={onContinueToUpsell}
        />
      );
    }

    return modal;
  }, [hasSeenOnboarding, isFreeTrialEligible, onClose, onContinueToUpsell]);

  return isUpsellModalOpen ? (
    <MultiWorkspacePlanSelectionOverlay
      analyticsAttributes={{ upsell: 'GAWelcomeModal' }}
      currentPlanSelected="premium"
      onClose={onClose}
      onCompleteUpsell={onClose}
    />
  ) : (
    shouldShowModal && (
      <Overlay
        className={styles.overlay}
        closeOnOutsideClick={false}
        onClose={noop}
      >
        {ModalComponent}
      </Overlay>
    )
  );
};
